/* Klarna on site message component
*/
import React, {useMemo, useRef} from 'react';
import {useDebounceEffect} from 'ahooks';

import {useLocalesMgr} from 'coreHooks';
import {loadScript} from 'coreUtils/utils';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';

import {IKlarnaMessageProps} from './IKlarnaMessage';

import styles from './KlarnaMessage.module.scss';

const KlarnaMessage = ({amount, dataIdentifier}: IKlarnaMessageProps) => {
	const {managerRepository} = useGeneralProvider();
	const localesMgr = useLocalesMgr();
	const klarnaCntr = useRef(null);
	const id = 'klarnaMessageLib';
	const currentLanguageCode: string = localesMgr.getCurrentLanguage().getLanguageCode();
	const currentCountryCode: string = localesMgr.getCurrentCountry().getCountryCode();
	const klarnaOnSiteMessageConfiguration = managerRepository
		.getConfigurationsMgr()
		.get('sitePreferences.klarnaOnSiteMessageConfiguration');
	const klarnaPlacementTypes = managerRepository.getConfigurationsMgr().get('sitePreferences.klarnaPlacementTypes');

	const klarnaConfig = useMemo(() => {
		const paymentRegion = currentCountryCode?.toLowerCase() === 'us' ? 'us' : 'eu';
		const region = paymentRegion.toUpperCase();
		let regionalConfig = {};

		if (klarnaOnSiteMessageConfiguration) {
			for (let key in klarnaOnSiteMessageConfiguration) {
				if (key === region) {
					regionalConfig = klarnaOnSiteMessageConfiguration[key];

					break;
				}
			}
		}

		return regionalConfig;
	}, [currentCountryCode]);

	const countryPlacement = useMemo(() => {
		let placementType = {};

		if (klarnaPlacementTypes) {
			for (let key in klarnaPlacementTypes) {
				if (key === currentCountryCode) {
					placementType = klarnaPlacementTypes[key];

					break;
				}
			}

			if (Object.keys(placementType).length === 0) {
				placementType = klarnaPlacementTypes['default'];
			}
		}

		return placementType;
	}, [currentCountryCode]);

	const initWidget = () => {
		if (klarnaConfig.link) {
			try {
				const klarnaPlacement = document.querySelector(`[data-identifier="${dataIdentifier}"]`);
				const klarnaScript = document.getElementById(id);

				if (klarnaScript) {
					klarnaScript.remove();

					const klarnaIframe = klarnaCntr.current.querySelector('iframe');

					if (klarnaIframe) {
						klarnaIframe.remove();
					}
				}

				if (klarnaPlacement) {
					klarnaPlacement.remove();
				}

				if (amount) {
					loadScript(klarnaConfig.link, id, {'client-id': klarnaConfig.clientID}, true);

					const newKlarna = document.createElement('klarna-placement');

					newKlarna.setAttribute('data-key', key);
					newKlarna.setAttribute('data-identifier', dataIdentifier);
					newKlarna.setAttribute('data-purchase-amount', amount * 100);
					newKlarna.setAttribute('data-locale', `${currentLanguageCode}-${currentCountryCode}`);
					klarnaCntr.current.append(newKlarna);
					window.KlarnaOnsiteService = window.KlarnaOnsiteService || [];
					window.KlarnaOnsiteService.push({eventName: 'refresh-placements'});
				}
			} catch (e) {
				console.warn('Unable to load klarna script.');
			}
		}
	};

	const key = countryPlacement?.key || '';

	useDebounceEffect(() => {
		initWidget();

		return () => {
			document.getElementById(id)?.remove();
		};
	}, [currentCountryCode, currentLanguageCode, amount], {
		wait: 50
	});

	if (!amount) {
		return <></>;
	}

	return <div className={styles['klarna__wrap']} ref={klarnaCntr}></div>;
};

export default KlarnaMessage;

